import React, { useState, useCallback, useEffect } from "react";
import { Trash2, Calendar, CheckCircle, XCircle } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { api } from "../ApiMapper/api";
import { toast } from "react-toastify";

const SUBSCRIPTION_TYPES = {
  PREMIUM: 'Premium (Ads Auto Renew + Email Automation)',
  ADS_AUTO_RENEW: 'Ads Auto Renew',
  EMAIL_AUTOMATION: 'Email Automation'
};

// Add this custom hook at the top level, before the Admin component
const usePolling = (callback, delay) => {
  const savedCallback = React.useRef();

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

// Add this component after the imports and before the Admin component
const DeleteConfirmationModal = ({ user, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4 text-gray-900">Confirm Delete</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete user <span className="font-semibold">{user.name}</span>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border rounded hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Delete User
          </button>
        </div>
      </div>
    </div>
  );
};

const SubscriptionForm = ({ user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    startDate: user?.subscription?.startDate || new Date().toISOString().split('T')[0],
    endDate: user?.subscription?.endDate || new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    type: user?.subscription?.type || SUBSCRIPTION_TYPES.PREMIUM
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/admin/subscription', {
        userId: user._id,
        ...formData
      });

      if (response.data.success) {
        // Pass the entire subscription data to onSuccess
        onSuccess(formData);
        toast.success('Subscription updated successfully');
      }
    } catch (error) {
      toast.error('Failed to update subscription: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Update Subscription</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subscription Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              required
            >
              {Object.values(SUBSCRIPTION_TYPES).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Update Subscription
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function Admin() {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUsers = async () => {
    try {
      const response = await api.get("/api/admin/users");
      const registeredUsers = response.data;

      const filteredUsers = registeredUsers.filter(user => user && user.user !== "ADMIN");

      setUsers(filteredUsers);
      console.log(filteredUsers);
    } catch (error) {
      toast.error("Error fetching users: " + error.message);
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  // Add polling for live updates
  usePolling(getUsers, 5000); // Poll every 5 seconds

  const toggleBotAccess = useCallback((userId, botKey) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId
          ? {
            ...user,
            bots: {
              ...user.bots,
              [botKey]: !user.bots[botKey],
            },
          }
          : user
      )
    );
  }, []);

  const updateSubscription = useCallback((userId, startDate, endDate) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId
          ? {
            ...user,
            subscription: {
              startDate,
              endDate,
            },
          }
          : user
      )
    );
  }, []);

  const handleDeleteUser = async (userId) => {
    try {
      const response = await api.delete(`/api/admin/users/${userId}`);
      if (response.data.success) {
        setUsers(prevUsers => prevUsers.filter(user => user._id !== userId));
        toast.success('User deleted successfully');
      }
    } catch (error) {
      toast.error('Failed to delete user: ' + error.message);
      console.error('Delete user error:', error);
    } finally {
      setUserToDelete(null);
    }
  };

  const toggleEditMode = useCallback((userId) => {
    setEditingUser(userId);
  }, []);

  // Update the getSubscriptionAccess function to also check subscription validity
  const getSubscriptionAccess = (subscription) => {
    if (!subscription) return { autoRenew: false, emailAutomation: false };

    const now = new Date();
    const endDate = new Date(subscription.endDate);
    const isActive = endDate > now;

    // Only grant access if subscription is active
    return {
      autoRenew: isActive && (
        subscription.type === SUBSCRIPTION_TYPES.PREMIUM || 
        subscription.type === SUBSCRIPTION_TYPES.ADS_AUTO_RENEW
      ),
      emailAutomation: isActive && (
        subscription.type === SUBSCRIPTION_TYPES.PREMIUM || 
        subscription.type === SUBSCRIPTION_TYPES.EMAIL_AUTOMATION
      )
    };
  };

  const handleSubscriptionUpdate = async (userId, subscriptionData) => {
    try {
      const response = await api.post('/api/admin/subscription', {
        userId,
        ...subscriptionData
      });
      
      if (response.data.success) {
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user._id === userId) {
              // Correctly access the subscription data from response
              const { subscription, botAccess } = response.data.data;
              return {
                ...user,
                subscription: subscription,
                bots: botAccess
              };
            }
            return user;
          })
        );
        
        toast.success('Subscription updated successfully');
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error('Failed to update subscription: ' + error.message);
      console.error('Subscription update error:', error);
    }
  };

  const toggleSubscriptionForm = (user) => {
    setSelectedUser(selectedUser?._id === user._id ? null : user);
  };

  // Add this new function for subscription status
  const getSubscriptionStatus = (user) => {
    if (!user.subscription) return {
      isActive: false,
      type: '',
      startDate: null,
      endDate: null
    };

    const now = new Date();
    const endDate = new Date(user.subscription.endDate);
    
    return {
      isActive: endDate > now,
      type: user.subscription.type || '',
      startDate: user.subscription.startDate,
      endDate: user.subscription.endDate
    };
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />

      <div className="flex-grow ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-8 border-b-4 border-blue-600 pb-3">
            Admin Dashboard
          </h2>

          <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
            <div className="bg-blue-600 text-white p-4">
              <h3 className="text-xl font-semibold">User Management</h3>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-100 text-blue-800">
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-center">Auto Renew</th>
                      <th className="px-4 py-3 text-center">Email Automation</th>
                      <th className="px-4 py-3 text-left">Subscription</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const { isActive, type } = getSubscriptionStatus(user);
                      const access = getSubscriptionAccess(user.subscription);
                      const now = new Date();
                      const endDate = new Date(user.subscription?.endDate);
                      const isExpired = endDate < now;
                      
                      return (
                        <tr key={user._id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">{user.name}</td>
                          <td className="px-4 py-3">{user.email}</td>
                          <td className="px-4 py-3 text-center">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={access.autoRenew}
                                readOnly
                              />
                              <div className={`relative w-11 h-6 ${access.autoRenew ? 'bg-blue-600' : 'bg-gray-200'} 
                                rounded-full peer peer-checked:after:translate-x-full after:content-[''] 
                                after:absolute after:top-[2px] after:start-[2px] after:bg-white 
                                after:border-gray-300 after:border after:rounded-full after:h-5 
                                after:w-5 after:transition-all`}
                              >
                                {isExpired && (
                                  <span className="absolute -top-4 right-0 text-xs text-red-500">
                                    Expired
                                  </span>
                                )}
                              </div>
                            </label>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={access.emailAutomation}
                                readOnly
                              />
                              <div className={`relative w-11 h-6 ${access.emailAutomation ? 'bg-blue-600' : 'bg-gray-200'} 
                                rounded-full peer peer-checked:after:translate-x-full after:content-[''] 
                                after:absolute after:top-[2px] after:start-[2px] after:bg-white 
                                after:border-gray-300 after:border after:rounded-full after:h-5 
                                after:w-5 after:transition-all`}
                              >
                                {isExpired && (
                                  <span className="absolute -top-4 right-0 text-xs text-red-500">
                                    Expired
                                  </span>
                                )}
                              </div>
                            </label>
                          </td>
                          <td className="px-4 py-3">
                            <div className={`text-sm ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                              <p>Type: {type || 'No subscription'}</p>
                              <p>Status: {isActive ? 'Active' : 'Inactive'}</p>
                              <p>Start: {user.subscription?.startDate ? formatDate(user.subscription.startDate) : 'N/A'}</p>
                              <p>End: {user.subscription?.endDate ? formatDate(user.subscription.endDate) : 'N/A'}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => setUserToDelete(user)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Remove User"
                              >
                                <Trash2 size={20} />
                              </button>
                              <button
                                onClick={() => toggleSubscriptionForm(user)}
                                className="text-blue-500 hover:text-blue-700 transition-colors"
                                title="Update Subscription"
                              >
                                <Calendar size={20} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 text-right">
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <span>Add New User</span>
                  <CheckCircle size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
        {selectedUser && (
          <SubscriptionForm
            user={selectedUser}
            onSuccess={(subscription) => handleSubscriptionUpdate(selectedUser._id, subscription)}
            onCancel={() => setSelectedUser(null)}
          />
        )}
        {userToDelete && (
          <DeleteConfirmationModal
            user={userToDelete}
            onConfirm={() => handleDeleteUser(userToDelete._id)}
            onCancel={() => setUserToDelete(null)}
          />
        )}
      </div>
    </div>
  );
}

export default Admin;
